import { createContext, JSX, ReactNode, useContext } from 'react';
import type { NavModuleProps } from 'vopay';
import useSubModule from '../hooks/useSubModule';

export type NavMenuCtx = {
  getModuleDescription: () => string;
  getModuleLabel: () => string;
  getModuleTitle: () => string;
  getParentModules: () => string[];
  getSubModules: (parentModule: string) => NavModuleProps[];
  goBack: () => void;
  isTopLevel: () => boolean;
  onNavClick: (module: string) => void;
  onSubNavClick: (moduleId: string, destination: string, currLabel: string) => void;
  setSubNavActiveModule: (activeModule: string) => void;
  reset: () => void;
};

export const NavMenuContext = createContext<NavMenuCtx>({
  getModuleDescription: () => '',
  getModuleLabel: () => '',
  getModuleTitle: () => '',
  getParentModules: () => [],
  getSubModules: () => [],
  goBack: () => {},
  isTopLevel: () => true,
  onNavClick: () => {},
  onSubNavClick: () => {},
  setSubNavActiveModule: () => '',
  reset: () => {},
});

export const NavMenuProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const {
    description,
    label,
    hasParent,
    parentModules,
    subNavLabel,
    getSubNavModules,
    setSubNavActiveModule,
    goBackInNavLeft,
    onNavClick,
    onSubNavClick,
    reset,
  } = useSubModule();

  const getModuleDescription = () => description;
  const getModuleLabel = () => label;
  const getModuleTitle = () => subNavLabel;
  const getParentModules = () => parentModules;
  const getSubModules = (parentModule?: string) => getSubNavModules(parentModule);
  const goBack = () => goBackInNavLeft();
  const isTopLevel = () => !hasParent;

  return (
    <NavMenuContext.Provider
      value={{
        getModuleDescription,
        getModuleLabel,
        getModuleTitle,
        getParentModules,
        getSubModules,
        goBack,
        isTopLevel,
        onNavClick,
        onSubNavClick,
        setSubNavActiveModule,
        reset,
      }}
    >
      {children}
    </NavMenuContext.Provider>
  );
};

export const useNavMenu = (): NavMenuCtx => useContext(NavMenuContext);
